import 'reflect-metadata';
import './sentry';
import {
  LoadingContainer,
  ScrollToTop,
  SnackbarProvider,
  SvgIconStyle,
  ThemeProvider,
} from '@pflegenavi/web-components';
import React, {
  type FC,
  type PropsWithChildren,
  StrictMode,
  Suspense,
} from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import App from './app/App';
import { initI18N, LocaleProvider } from '@pflegenavi/frontend/localization';
import {
  localeStorage,
  LocalizationProvider,
  ThemeLocalization,
} from '@pflegenavi/frontend/localization-web-app';
import ErrorBoundary from './components/ErrorBoundry';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { DndProvider } from 'react-dnd';
import {
  AuthenticationProvider,
  useAuthenticationInternal,
} from '@pflegenavi/frontend/authentication';
import { setSentryAuthContext, setSentryApiUrl } from './sentry';
import { keycloak, login } from './keycloak';

// this is a virtual font file from vite pluging see: https://github.com/stafyniaksacha/vite-plugin-fonts
import 'unfonts.css';
import {
  QueryCache,
  QueryClient,
  QueryClientProvider,
} from '@tanstack/react-query';
import {
  KycPhoenixApiProvider,
  LineItemTaxApiProvider,
  LineItemTaxPhoenixApiProvider,
  OnboardingApiProvider,
  OnboardingPhoenixApiProvider,
  PaymentPhoenixApiProvider,
  ReceiptBatchApiProvider,
  ReceiptBatchPhoenixApiProvider,
  ResidentPhoenixApiProvider,
  ServiceProviderApiProvider,
  ServiceProviderAppPhoenixApiProvider,
  ServiceProviderPaymentsApiProvider,
  ServiceProviderPhoenixApiProvider,
  ServiceProviderReceiptUploadApiProvider,
  ServiceProviderStripeApiProvider,
  StripePhoenixApiProvider,
  UserProfileApiProvider,
  useServiceProvider,
} from '@pflegenavi/service-provider-frontend/api';
import {
  UserProfileApiProvider as NursingHomeUserProfileApiProvider,
  ServiceProviderPhoenixApiProvider as NursingHomeServiceProviderPhoenixApiProvider,
  UserProfilePhoenixApiProvider,
} from '@pflegenavi/frontend/api-nursing-home';
import { TenantContextProvider } from '@pflegenavi/frontend/tenant';
import * as Sentry from '@sentry/react';
import { EmptyTrackingProvider } from '@pflegenavi/frontend/tracking';

import dashboardIcon from './assets/dashboard_24dp.svg';

import {
  QueryClient as TanstackQueryClient,
  QueryClientProvider as TanstackQueryClientProvider,
} from '@tanstack/react-query';
import { spAppMainPages } from '@pflegenavi/frontend/routing';
import {
  getMobileOSWithSizeHeuristic,
  getTokenPayloadFromToken,
  getWebWelcomeToken,
  MobileOS,
} from '@pflegenavi/shared/utils';
import { DownloadAppFromStore } from './components/general/DownloadAppFromStore';
import { ClientStoredResourcesProvider } from '@pflegenavi/frontend/client-stored-resources-context';
import {
  PflegenaviAuthenticationProvider,
  shouldUseNewLogin,
  useAuthentication,
} from '@pflegenavi/shared-frontend/authentication';
import { getPhoenixApiBaseUrl } from '@pflegenavi/shared-frontend/platform';
import type { NavBarExtension } from '@pflegenavi/frontend/layout';
import { ExtensionProvider } from '@pflegenavi/frontend/layout';
import { useTranslation } from 'react-i18next';
import { useGetGlobalServiceProviderId } from './hooks/useGetGlobalServiceProviderId';

const tanstackQueryClient = new TanstackQueryClient();

window.global ||= window;

// @ts-expect-error // pflegenaviApplication is untyped
window.global.pflegenaviApplication = 'service-provider-web';
// @ts-expect-error // pflegenaviApplicationVersion is untyped
window.global.pflegenaviApplicationVersion =
  // @ts-expect-error // SENTRY_RELEASE is untyped
  typeof SENTRY_RELEASE !== 'undefined' ? SENTRY_RELEASE.id : 'local';

await initI18N(localeStorage);

const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: (error) => {
      Sentry.captureException(error);
    },
  }),
  defaultOptions: {
    queries: {
      gcTime: 1000 * 60 * 60 * 24, // 24 hours
      refetchOnWindowFocus: false,
      refetchInterval: 1000 * 60 * 5,
      staleTime: 0, //1000 * 60 * 5, // 5 minutes
    },
    mutations: {
      onError: (error) => {
        Sentry.captureException(error);
      },
    },
  },
});

const container = document.getElementById('root');

const root = createRoot(container!);

// Component to set global auth context for Sentry feedback
const SetSentryAuth: FC<PropsWithChildren> = ({ children }) => {
  const authContext = useAuthenticationInternal();
  React.useEffect(() => {
    setSentryAuthContext(authContext);
  }, [authContext]);
  return <>{children}</>;
};

const AuthWrapper: FC<PropsWithChildren> = ({ children }) => {
  const auth = useAuthentication(shouldUseNewLogin());

  return (
    <AuthenticationProvider
      // @ts-expect-error // keycloak type mismatch
      keycloak={auth ?? keycloak}
      login={login}
      redirectUri={`${window.location.origin}/${spAppMainPages.DASHBOARD}`}
      isNewAuth={Boolean(auth)}
    >
      <SetSentryAuth>{children}</SetSentryAuth>
    </AuthenticationProvider>
  );
};

function redirectIfNecessary(url: string) {
  const hostname = new URL(url).hostname;

  if (hostname !== window.location.hostname) {
    window.location.replace(url);
    return 'redirected';
  }
  return undefined;
}

function renderApp() {
  const mobileOs = getMobileOSWithSizeHeuristic();
  if (mobileOs === MobileOS.Android || mobileOs === MobileOS.iOS) {
    root.render(
      <ThemeProvider>
        <LocaleProvider localeStorage={localeStorage}>
          <LocalizationProvider>
            <ThemeLocalization>
              <DownloadAppFromStore platform={mobileOs} />
            </ThemeLocalization>
          </LocalizationProvider>
        </LocaleProvider>
      </ThemeProvider>
    );
  } else {
    const apiUrl = getPhoenixApiBaseUrl(undefined);
    setSentryApiUrl(apiUrl);

    root.render(
      <ClientStoredResourcesProvider storage={localStorage}>
        <PflegenaviAuthenticationProvider
          storage={localStorage}
          getWelcomeToken={getWebWelcomeToken}
          shouldUseNewLogin={shouldUseNewLogin()}
          apiUrl={apiUrl}
          redirectIfNecessary={redirectIfNecessary}
          onLogout={() => {
            queryClient.clear();
            tanstackQueryClient.clear();
          }}
        >
          <AuthWrapper>
            <StrictMode>
              <BrowserRouter>
                <SnackbarProvider>
                  <QueryClientProvider client={queryClient}>
                    <TanstackQueryClientProvider client={tanstackQueryClient}>
                      <TenantContextProvider tenantId="service_provider">
                        <ThemeProvider>
                          <LocaleProvider localeStorage={localeStorage}>
                            <LocalizationProvider>
                              <ServiceProviderPhoenixApiProvider>
                                <UserProfileApiProvider>
                                  <UserProfilePhoenixApiProvider>
                                    <KycPhoenixApiProvider>
                                      {/* The nursing home user profile api provider is used by the feature flags. It actually points to the same endpoint as the service provider user profile api provider */}
                                      <NursingHomeUserProfileApiProvider>
                                        <OnboardingApiProvider>
                                          <OnboardingPhoenixApiProvider>
                                            <NursingHomeServiceProviderPhoenixApiProvider>
                                              <ServiceProviderApiProvider>
                                                <ServiceProviderAppPhoenixApiProvider
                                                  apiUrl={getPhoenixApiBaseUrl(
                                                    undefined
                                                  )}
                                                >
                                                  <ServiceProviderStripeApiProvider>
                                                    <StripePhoenixApiProvider>
                                                      <ServiceProviderPaymentsApiProvider>
                                                        <PaymentPhoenixApiProvider>
                                                          <ServiceProviderReceiptUploadApiProvider>
                                                            <ReceiptBatchApiProvider>
                                                              <ReceiptBatchPhoenixApiProvider>
                                                                <LineItemTaxApiProvider>
                                                                  <LineItemTaxPhoenixApiProvider>
                                                                    <ResidentPhoenixApiProvider>
                                                                      <ThemeLocalization>
                                                                        <DndProvider
                                                                          backend={
                                                                            HTML5Backend
                                                                          }
                                                                        >
                                                                          <NavExtension>
                                                                            <ScrollToTop />
                                                                            <ErrorBoundary>
                                                                              <EmptyTrackingProvider>
                                                                                <Suspense
                                                                                  fallback={
                                                                                    <LoadingContainer />
                                                                                  }
                                                                                >
                                                                                  <App />
                                                                                </Suspense>
                                                                              </EmptyTrackingProvider>
                                                                            </ErrorBoundary>
                                                                          </NavExtension>
                                                                        </DndProvider>
                                                                      </ThemeLocalization>
                                                                    </ResidentPhoenixApiProvider>
                                                                  </LineItemTaxPhoenixApiProvider>
                                                                </LineItemTaxApiProvider>
                                                              </ReceiptBatchPhoenixApiProvider>
                                                            </ReceiptBatchApiProvider>
                                                          </ServiceProviderReceiptUploadApiProvider>
                                                        </PaymentPhoenixApiProvider>
                                                      </ServiceProviderPaymentsApiProvider>
                                                    </StripePhoenixApiProvider>
                                                  </ServiceProviderStripeApiProvider>
                                                </ServiceProviderAppPhoenixApiProvider>
                                              </ServiceProviderApiProvider>
                                            </NursingHomeServiceProviderPhoenixApiProvider>
                                          </OnboardingPhoenixApiProvider>
                                        </OnboardingApiProvider>
                                      </NursingHomeUserProfileApiProvider>
                                    </KycPhoenixApiProvider>
                                  </UserProfilePhoenixApiProvider>
                                </UserProfileApiProvider>
                              </ServiceProviderPhoenixApiProvider>
                            </LocalizationProvider>
                          </LocaleProvider>
                        </ThemeProvider>
                        <ReactQueryDevtools initialIsOpen={false} />
                      </TenantContextProvider>
                    </TanstackQueryClientProvider>
                  </QueryClientProvider>
                </SnackbarProvider>
              </BrowserRouter>
            </StrictMode>
          </AuthWrapper>
        </PflegenaviAuthenticationProvider>
      </ClientStoredResourcesProvider>
    );
  }
}

const getIcon = (url: string) => (
  <SvgIconStyle
    src={url}
    sx={{
      width: 1,
      height: 1,
    }}
  />
);

const NavExtension: FC<PropsWithChildren> = ({ children }) => {
  const { getTokenImmediate } = useAuthenticationInternal();

  if (!getTokenImmediate()) {
    return children;
  }

  return <NavExtensionInner>{children}</NavExtensionInner>;
};

const NavExtensionInner: FC<PropsWithChildren> = ({ children }) => {
  const { t } = useTranslation();

  const { data: serviceProvider } = useServiceProvider({
    globalServiceProviderId:
      useGetGlobalServiceProviderId().globalServiceProviderId,
  });

  const extensions: NavBarExtension[] = [];
  if (serviceProvider?.paymentProcessor === 'stripe') {
    extensions.push({
      item: {
        title: t('dashboard.main'),
        path: spAppMainPages.DASHBOARD,
        icon: getIcon(dashboardIcon),
        children: [
          {
            title: t('sp-web-app.receipt-batch.overview'),
            path: spAppMainPages.DASHBOARD,
          },
          {
            title: t('sp-web-app.receipt-batch.new'),
            path: spAppMainPages.RECEIPT_BATCH_NEW,
          },
        ],
      },
    });
  } else {
    extensions.push({
      item: {
        title: t('dashboard.main'),
        path: spAppMainPages.DASHBOARD,
        icon: getIcon(dashboardIcon),
        children: [
          {
            title: t('sp-web-app.receipt-batch.overview'),
            path: spAppMainPages.DASHBOARD,
          },
          {
            title: t('sp-web-app.receipt-batch.new'),
            path: spAppMainPages.RECEIPT_BATCH_NEW,
          },
          {
            title: t('sp-web-app.payouts'),
            path: spAppMainPages.PAYOUTS,
          },
        ],
      },
    });
  }

  return <ExtensionProvider value={extensions}>{children}</ExtensionProvider>;
};

function redirectWelcome() {
  const queryParams = new URLSearchParams(window.location.search);
  const key = queryParams.get('key');
  let basePath = 'https://auth.pflegenavi.at';

  if (window.location.host === 'localhost:3003') {
    basePath = 'http://localhost:5051';
  } else if (
    window.location.hostname === 'service-provider.staging.pflegenavi.at'
  ) {
    basePath = 'https://auth-staging.pflegenavi.at';
  }

  window.location.href = `${basePath}/auth/realms/service_provider/login-actions/action-token?key=${key}`;
}

if (window.location.pathname !== '/welcome') {
  renderApp();
} else {
  const search = new URLSearchParams(window.location.search);
  const token = search.get('key');
  const payload = getTokenPayloadFromToken(token);
  if (token && payload?.v !== 'pn0.1') {
    redirectWelcome();
  } else {
    renderApp();
  }
}
